import { useCallback, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { ActionFlag } from '@makyoapp/makyo-common'
import { DeleteModal, Modal, ModalGridFooter } from '@makyoapp/makyo-ui'

import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useAppPagesContext } from '@/context/AppPagesContext'

function BuilderDelPageModal() {
  const { setPageAction, pageAction } = useAppBuilderContext()
  const { deletePage, pages } = useAppPagesContext()

  const { t } = useTranslation()
  const onHide = useCallback(() => {
    setPageAction(undefined)
  }, [setPageAction])
  const thePage = useMemo(() => {
    return pages?.find((p) => p.id === pageAction?.page)
  }, [pages, pageAction])

  const pageTitle = thePage?.title ?? ''
  const isDel = pageAction?.flag === ActionFlag.Delete

  return isDel ? (
    <DeleteModal
      show={true}
      onHide={onHide}
      onSubmit={async () => {
        if (isDel) {
          await deletePage(pageAction.page).then(() => {
            onHide()
          })
        }
      }}
      continueLabel={t('Yes, Delete')}
      cancelLabel={t('Cancel')}
      withCancel
      title={t('Delete Page')}
    >
      <div className='modal-sm text-sm'>
        <Trans values={{ pageTitle }}>
          Are you sure you would like to delete <strong>{pageTitle}</strong> ?
        </Trans>
      </div>
    </DeleteModal>
  ) : null
}

function BuilderDelMenuModal() {
  const { setMenuAction, menuAction, deleteMenu } = useAppBuilderContext()

  const { t } = useTranslation()

  const onHide = useCallback(() => {
    setMenuAction(undefined)
  }, [setMenuAction])

  const menuTitle = menuAction?.menu.title ?? ''
  const isDel = menuAction?.flag === ActionFlag.Delete

  return isDel ? (
    <DeleteModal
      show={true}
      onHide={onHide}
      onSubmit={async () => {
        if (isDel) {
          await deleteMenu(menuAction.menu.id).then(() => {
            onHide()
          })
        }
      }}
      continueLabel={t('Yes, Delete')}
      cancelLabel={t('Cancel')}
      withCancel
      title={t('Delete Menu')}
    >
      <div className='modal-sm text-sm'>
        <Trans values={{ menuTitle }}>
          Are you sure you would like to delete <strong>{menuTitle}</strong> ?
        </Trans>
      </div>
    </DeleteModal>
  ) : null
}

function BuilderDelMenuItemModal() {
  const { menuAction, setMenuItemAction, menuItemAction } = useAppBuilderContext()
  const { t } = useTranslation()
  const onHide = useCallback(() => {
    setMenuItemAction(undefined)
  }, [setMenuItemAction])

  const menu = menuAction?.menu
  const menuItem = menu?.items.find((item) => item.id === menuItemAction?.menuItemId)
  const menuTitle = menuItem?.title ?? ''

  const isDel = menuItemAction?.flag === ActionFlag.Delete
  return isDel ? (
    <DeleteModal
      show={true}
      onHide={onHide}
      onSubmit={async () => {
        if (isDel) {
          /*await deleteMenuItem(menuItemAction.menuItem.id).then(() => {
            onHide()
          })*/
        }
      }}
      continueLabel={t('Yes, Delete')}
      cancelLabel={t('Cancel')}
      withCancel
      title={t('Delete Menu Item')}
    >
      <div className='modal-sm text-sm'>
        <Trans values={{ menuTitle }}>
          Are you sure you would like to delete <strong>{menuTitle}</strong> ?
        </Trans>
      </div>
    </DeleteModal>
  ) : null
}

function BuilderDelDialogModal() {
  const { setDialogAction, dialogAction, deleteDialog } = useAppBuilderContext()
  const { t } = useTranslation()

  const onHide = useCallback(() => {
    setDialogAction(undefined)
  }, [setDialogAction])
  const dialogTitle = dialogAction?.dialog.name ?? ''
  const isDel = dialogAction?.flag === ActionFlag.Delete

  return isDel ? (
    <DeleteModal
      show={true}
      onHide={onHide}
      onSubmit={async () => {
        if (isDel) {
          await deleteDialog(dialogAction.dialog.id).then(() => {
            onHide()
          })
        }
      }}
      continueLabel={t('Yes, Delete')}
      cancelLabel={t('Cancel')}
      withCancel
      title={t('Delete Dialog')}
    >
      <div className='modal-sm text-sm'>
        <Trans values={{ dialogTitle }}>
          Are you sure you would like to delete <strong>{dialogTitle}</strong> ?
        </Trans>
      </div>
    </DeleteModal>
  ) : null
}

function BuilderFabLimitModal() {
  const { t } = useTranslation()
  const { showFabWarningModal, setShowFabWarningModal, fabWarningReason } = useAppBuilderContext()

  return (
    <>
      {showFabWarningModal && (
        <Modal show={showFabWarningModal}>
          <Modal.Header onCloseClick={() => setShowFabWarningModal(false)}>
            <Trans>{fabWarningReason === 'header' ? 'Action Not Allowed' : 'FAB Limit Reached'}</Trans>
          </Modal.Header>
          <Modal.Body className='modal-sm'>
            <div className='py-2'>
              {fabWarningReason === 'header' ? (
                <Trans>
                  {`You cannot add a Floating Action Button (FAB) inside the Global Header. Place the FAB in the page
                  body or footer instead.`}
                </Trans>
              ) : (
                <Trans>
                  {`You cannot add more than one Floating Action Button (FAB) on a screen. To include additional actions,
                  please use the 'Secondary FAB' option within the existing FAB component.`}
                </Trans>
              )}
            </div>
          </Modal.Body>
          <ModalGridFooter
            cancelLabel={t('Cancel')}
            onCancel={() => setShowFabWarningModal(false)}
            onHide={() => setShowFabWarningModal(false)}
            onSubmit={() => setShowFabWarningModal(false)}
            primaryLabel={t('Got it')}
          />
        </Modal>
      )}
    </>
  )
}

function BuilderDateTimeValidationModal() {
  const { t } = useTranslation()
  const { showDateTimeValidationModal, setShowDateTimeValidationModal } = useAppBuilderContext()

  return (
    <>
      {showDateTimeValidationModal && (
        <Modal show={showDateTimeValidationModal}>
          <Modal.Header onCloseClick={() => setShowDateTimeValidationModal(false)}>
            <Trans>Invalid Time Range</Trans>
          </Modal.Header>
          <Modal.Body className='modal-sm'>
            <div className='py-2'>
              <Trans>
                The end time cannot be earlier than or equal to the start time. Please select a valid time range.
              </Trans>
            </div>
          </Modal.Body>
          <ModalGridFooter
            onHide={() => setShowDateTimeValidationModal(false)}
            onSubmit={() => setShowDateTimeValidationModal(false)}
            primaryLabel={t('Got it')}
          />
        </Modal>
      )}
    </>
  )
}

/**
 * contains all platform-related modals within app builder
 *
 * @todo:
 *  - use lazymodal
 *  - implement parent-controlled/stateless modal system
 */
export default function BuilderModals() {
  return (
    <>
      <BuilderFabLimitModal />
      <BuilderDelPageModal />
      <BuilderDelMenuModal />
      <BuilderDelMenuItemModal />
      <BuilderDelDialogModal />
      <BuilderDateTimeValidationModal />
    </>
  )
}
